<template>
  <JoszakiModal
    :title="$t('conductor.dataTable.addFilter')"
    :open="isOpen"
    size="md"
    @cancel="close"
    @close="close"
    @confirm="save"
  >
    <template #body>
      <div v-if="filterValue" class="flex flex-col gap-2 min-h-[150px]">
        <JoszakiDropdown
          v-model="filterValue.operator"
          :items="operatorListItems"
        />

        <template v-if="isComposite">
          <div v-for="field in filterOption.fields" :key="field.key">
            <component
              :is="autocompleteComponents[field.fieldType]"
              v-if="autocompleteComponents[field.fieldType]"
              :label="field.label"
              :value="filterValue.fields[field.key]"
              @input="setFieldValue(field.key, $event)"
            />
            <JoszakiInput
              v-else
              :value="filterValue.fields[field.key]"
              :type="field.fieldType"
              :label="field.label"
              :unit="field.unit"
              @input="setFieldValue(field.key, $event)"
            />
          </div>
        </template>
        <component
          :is="autocompleteComponents[filterOption.fieldType]"
          v-else-if="isAutocomplete"
          :value="autocompleteValue"
          @input="setAutocompleteValue"
        />
        <JoszakiAutocomplete
          v-else-if="isSelect"
          :items="filterOption.items"
          :value="autocompleteValue"
          :allow-new="filterOption.fieldType === 'selectOrText'"
          local-filter
          @input="setAutocompleteValue"
        />

        <JoszakiCheckbox
          v-else-if="filterOption.fieldType === 'checkbox'"
          v-model="filterValue.value"
          :label="filterOption.label"
        />
        <JoszakiInput
          v-else-if="isSimpleValue"
          v-model="filterValue.value"
          :type="filterOption.fieldType"
          :unit="filterOption.unit"
        />
        <JoszakiInput
          v-if="filterValue.operator === OPERATORS.BETWEEN"
          v-model="filterValue.min"
          :type="filterOption.fieldType"
        />
        <JoszakiInput
          v-if="filterValue.operator === OPERATORS.BETWEEN"
          v-model="filterValue.max"
          :type="filterOption.fieldType"
        />
      </div>
    </template>
  </JoszakiModal>
</template>

<script>
import { useContext } from "@nuxtjs/composition-api";
import { computed, ref } from "vue";
import isNil from "lodash/isNil";
import AutocompleteServiceType from "~/components/_refactored/autocomplete/AutocompleteServiceType.vue";
import { useDataTableFilterOperators } from "~/components/_refactored/conductor/data-table/useDataTableFilterOperators";
import AutocompleteCity from "~/components/_refactored/autocomplete/AutocompleteCity.vue";
import AutocompleteLabel from "~/components/_refactored/autocomplete/AutocompleteLabel.vue";
import AutocompleteMiniMarket from "~/components/_refactored/autocomplete/AutocompleteMiniMarket.vue";
import AutocompleteMiniMarketTeamAdmin from "~/components/_refactored/autocomplete/AutocompleteMiniMarketTeamAdmin.vue";
import AutocompletePartner from "~/components/_refactored/autocomplete/AutocompletePartner.vue";
import AutocompletePersonAdmin from "~/components/_refactored/autocomplete/AutocompletePersonAdmin.vue";
import AutocompleteProfession from "~/components/_refactored/autocomplete/AutocompleteProfession.vue";
import AutocompleteProfessionTask from "~/components/_refactored/autocomplete/AutocompleteProfessionTask.vue";
import AutocompleteProfessionGroupAdmin from "~/components/_refactored/autocomplete/AutocompleteProfessionGroupAdmin.vue";
import AutocompleteZone from "~/components/_refactored/autocomplete/AutocompleteZone.vue";
import AutocompleteSubcounty from "~/components/_refactored/autocomplete/AutocompleteSubcounty.vue";

export default {
  props: {
    filterOptions: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { i18n, $joszaki } = useContext();
    const isOpen = ref(false);
    const filterValue = ref(null);
    const index = ref(null);
    const OPERATORS = useDataTableFilterOperators();

    const filterOption = computed(() => {
      if (!filterValue.value) {
        return null;
      }

      return props.filterOptions.find(
        (option) => option.id === filterValue.value.filterId
      );
    });

    const autocompleteComponents = {
      city: AutocompleteCity,
      label: AutocompleteLabel,
      miniMarket: AutocompleteMiniMarket,
      miniMarketTeam: AutocompleteMiniMarketTeamAdmin,
      partner: AutocompletePartner,
      person: AutocompletePersonAdmin,
      profession: AutocompleteProfession,
      professionGroup: AutocompleteProfessionGroupAdmin,
      professionTask: AutocompleteProfessionTask,
      zone: AutocompleteZone,
      subcounty: AutocompleteSubcounty,
      serviceType: AutocompleteServiceType,
    };
    const isAutocomplete = computed(() => {
      return Object.keys(autocompleteComponents).includes(
        filterOption.value?.fieldType
      );
    });

    const isSelect = computed(() => {
      return ["select", "selectOrText"].includes(filterOption.value?.fieldType);
    });

    const isComposite = computed(() => {
      return !!filterOption.value?.fields?.length;
    });

    const isMultiSelect = computed(() => {
      return (
        filterValue.value?.operator === OPERATORS.IN ||
        filterValue.value?.operator === OPERATORS.NOT_IN
      );
    });

    const isSimpleValue = computed(() => {
      return [
        OPERATORS.EQUALS,
        OPERATORS.GREATER_THAN,
        OPERATORS.GREATER_THAN_OR_EQ,
        OPERATORS.LESS_THAN,
        OPERATORS.LESS_THAN_OR_EQ,
        OPERATORS.NOT_EQUALS,
        OPERATORS.LIKE,
      ].includes(filterValue.value.operator);
    });

    const operatorListItems = computed(() => {
      return (filterOption.value?.operators ?? [OPERATORS.EQUALS]).map(
        (operator) => ({
          value: operator,
          label: i18n.t(`conductor.dataTable.operatorNames.${operator}`, {
            field: filterOption.value?.label,
          }),
        })
      );
    });

    const open = (value, i) => {
      const option = props.filterOptions.find(
        (candidate) => candidate.id === value.filterId
      );

      filterValue.value = {
        ...value,
        fields: option?.fields
          ? Object.fromEntries(
              option.fields.map((field) => [
                field.key,
                value.fields?.[field.key] ?? null,
              ])
            )
          : value.fields,
      };
      index.value = i;
      isOpen.value = true;
    };

    const close = () => {
      isOpen.value = false;
      index.value = null;
      filterValue.value = null;
    };

    const save = () => {
      const isValid =
        !isNil(filterValue.value) &&
        (!isComposite.value ||
          filterOption.value.fields.every((field) => {
            const subValue = filterValue.value.fields?.[field.key];
            return !isNil(subValue) && subValue !== "";
          }));

      if (!isValid) {
        $joszaki.toast({
          message: i18n.t("conductor.dataTable.selectValue"),
          type: "warning",
        });
        return;
      }
      emit("save", filterValue.value, index.value);
      close();
    };

    const setFieldValue = (key, value) => {
      filterValue.value.fields[key] = value;
    };

    // Items typed into an allow-new picker come back without an id, but the
    // filter payload is built from the id, so the typed text becomes both.
    const withIdFromName = (item) => {
      if (!item || typeof item !== "object" || item.id) {
        return item;
      }
      return { ...item, id: item.name };
    };

    const setAutocompleteValue = (value) => {
      if (isMultiSelect.value) {
        const items = value ?? [];
        filterValue.value.items = items.some((item) => item && !item.id)
          ? items.map(withIdFromName)
          : items;
      } else {
        filterValue.value.value = withIdFromName(value);
      }
    };

    const autocompleteValue = computed(() => {
      if (isMultiSelect.value) {
        return filterValue.value.items;
      }
      return filterValue.value.value;
    });

    return {
      OPERATORS,
      isOpen,
      open,
      close,
      save,
      operatorListItems,
      filterOption,
      isMultiSelect,
      isAutocomplete,
      isSelect,
      isComposite,
      isSimpleValue,
      setFieldValue,
      autocompleteComponents,
      filterValue,
      setAutocompleteValue,
      autocompleteValue,
    };
  },
};
</script>
